import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Lock, GripVertical, Tag, SlidersHorizontal } from "lucide-react";

const slug = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24) || "fase";

function AcqSelect({ stages, value, onChange, tid }) {
  const v = stages.some((s) => s.key === value) ? value : "chiuso";
  return (
    <div className="mt-5 rounded-xl bg-emerald-50/60 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20 p-4 grid sm:grid-cols-[1fr_260px] gap-3 items-center">
      <div>
        <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Fase di acquisizione</div>
        <p className="text-xs text-slate-500">Una scheda conta come acquisita da questa fase in poi: vale per obiettivi, bonus, compensi e data di acquisizione.</p>
      </div>
      <Select value={v} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue /></SelectTrigger>
        <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function StageEditor({ stages, setStages, tid = "" }) {
  const chiusoIdx = stages.findIndex((s) => s.key === "chiuso");
  const setLabel = (i, v) => setStages((arr) => arr.map((s, idx) => (idx === i ? { ...s, label: v } : s)));
  const removeStage = (i) => {
    if (stages[i].key === "chiuso") return;
    setStages((arr) => arr.filter((_, idx) => idx !== i));
  };
  const moveStage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    if (stages[i].key === "chiuso" || stages[j].key === "chiuso") return;
    setStages((arr) => {
      const copy = [...arr];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };
  const addStage = () => {
    const newStage = { key: `${slug("fase")}-${Math.random().toString(36).slice(2, 6)}`, label: "Nuova fase" };
    setStages((arr) => {
      const ci = arr.findIndex((s) => s.key === "chiuso");
      if (ci === -1) return [...arr, newStage];
      const copy = [...arr];
      copy.splice(ci, 0, newStage);
      return copy;
    });
  };
  return (
    <>
      <div className="space-y-2">
        {stages.map((s, i) => {
          const isChiuso = s.key === "chiuso";
          return (
            <div key={s.key} data-testid={`${tid}stage-row-${i}`} className="flex items-center gap-2">
              <span className="text-slate-300"><GripVertical className="h-4 w-4" /></span>
              <span className="w-6 text-center text-xs font-bold text-slate-400 tabular-nums">{i + 1}</span>
              <Input data-testid={`${tid}stage-label-${i}`} value={s.label} onChange={(e) => setLabel(i, e.target.value)} className="flex-1" placeholder="Nome fase" />
              {isChiuso ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full"><Lock className="h-3 w-3" /> Ultima fase</span>
              ) : (
                <div className="flex items-center gap-1">
                  <button data-testid={`${tid}stage-up-${i}`} onClick={() => moveStage(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-primary disabled:opacity-30 p-1"><ArrowUp className="h-4 w-4" /></button>
                  <button data-testid={`${tid}stage-down-${i}`} onClick={() => moveStage(i, 1)} disabled={i >= chiusoIdx - 1} className="text-slate-400 hover:text-primary disabled:opacity-30 p-1"><ArrowDown className="h-4 w-4" /></button>
                  <button data-testid={`${tid}stage-del-${i}`} onClick={() => removeStage(i)} className="text-slate-300 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Button variant="outline" onClick={addStage} className="rounded-full gap-1.5 border-dashed mt-3" data-testid={`${tid}add-stage-button`}>
        <Plus className="h-4 w-4" /> Aggiungi fase
      </Button>
    </>
  );
}

const cleanStages = (stages) => {
  const cleaned = stages.map((s) => ({ key: s.key || slug(s.label), label: (s.label || "").trim() || s.key })).filter((s) => s.key);
  const chiuso = cleaned.find((s) => s.key === "chiuso") || { key: "chiuso", label: "Chiuso" };
  return [...cleaned.filter((s) => s.key !== "chiuso"), chiuso];
};

export default function Impostazioni() {
  const { settings, api, setSettings, refresh, activeSettore } = useCrm();
  const { isTitolare } = useAuth();
  const [stages, setStages] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [lavTipi, setLavTipi] = useState([]);
  const [stagesPartner, setStagesPartner] = useState([]);
  const [acq, setAcq] = useState("chiuso");
  const [acqPartner, setAcqPartner] = useState("chiuso");
  const [saving, setSaving] = useState(false);

  const isRitr = settings?.settore === "ritrattistica";

  useEffect(() => {
    if (settings) {
      setStages((settings.stages || []).map((s) => ({ ...s })));
      setTipi([...(settings.tipi_progetto || [])]);
      setLavTipi([...(settings.tipi_lavorazione || [])]);
      setStagesPartner((settings.stages_partner || []).map((s) => ({ ...s })));
      setAcq(settings.stage_acquisizione || "chiuso");
      setAcqPartner(settings.stage_acquisizione_partner || "chiuso");
    }
  }, [settings]);

  if (!isTitolare) {
    return <div className="text-slate-400 py-12 text-center" data-testid="impostazioni-denied">Sezione riservata al titolare.</div>;
  }

  const save = async () => {
    const payload = { stages: cleanStages(stages) };
    payload.stage_acquisizione = payload.stages.some((x) => x.key === acq) ? acq : "chiuso";
    if (isRitr) {
      payload.tipi_progetto = tipi.map((t) => (t || "").trim()).filter(Boolean);
      payload.stages_partner = cleanStages(stagesPartner);
      payload.stage_acquisizione_partner = payload.stages_partner.some((x) => x.key === acqPartner) ? acqPartner : "chiuso";
    } else {
      payload.tipi_lavorazione = lavTipi.map((t) => (t || "").trim()).filter(Boolean);
    }
    setSaving(true);
    try {
      const s = await api.updateSettings(payload);
      setSettings(s);
      await refresh();
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="impostazioni-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <SlidersHorizontal className="h-7 w-7 text-primary" /> Impostazioni Settore
          </h1>
          <p className="text-slate-500 mt-1">
            Stai configurando il settore <b className="text-slate-700 dark:text-slate-200 capitalize">{activeSettore}</b>. Usa il selettore in alto per cambiare settore.
          </p>
        </div>
        <Button data-testid="save-impostazioni-button" onClick={save} disabled={saving} className="rounded-full gap-1.5">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
      </div>

      <Card className="p-6 animate-fade-up">
        <h2 className="text-lg font-semibold mb-1">Fasi della pipeline{isRitr ? " — Clienti Privati" : ""}</h2>
        <p className="text-xs text-slate-500 mb-4">Rinomina, riordina o aggiungi le fasi del funnel. L'ultima fase <b>"Chiuso"</b> rappresenta l'acquisizione: può essere rinominata ma non eliminata né spostata.</p>
        <StageEditor stages={stages} setStages={setStages} />
        <AcqSelect stages={stages} value={acq} onChange={setAcq} tid="acq-stage-select" />
      </Card>

      {isRitr && (
        <Card className="p-6 animate-fade-up" data-testid="stages-partner-card">
          <h2 className="text-lg font-semibold mb-1">Fasi della pipeline — Partner</h2>
          <p className="text-xs text-slate-500 mb-4">Il funnel delle collaborazioni con parrucchieri, beauty farm, palestre… L'ultima fase indica l'accordo attivo.</p>
          <StageEditor stages={stagesPartner} setStages={setStagesPartner} tid="partner-" />
          <AcqSelect stages={stagesPartner} value={acqPartner} onChange={setAcqPartner} tid="acq-stage-partner-select" />
        </Card>
      )}

      {isRitr && (
        <Card className="p-6 animate-fade-up" data-testid="tipi-progetto-card">
          <h2 className="text-lg font-semibold mb-1">Tipi di servizio / experience</h2>
          <p className="text-xs text-slate-500 mb-4">Le tipologie selezionabili per i clienti privati e per le Portrait Experience.</p>
          <div className="space-y-2">
            {tipi.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`tipo-row-${i}`}>
                <Tag className="h-4 w-4 text-primary flex-shrink-0" />
                <Input data-testid={`tipo-input-${i}`} value={t} onChange={(e) => setTipi((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))} className="flex-1" placeholder="Nome tipo progetto" />
                <button data-testid={`tipo-del-${i}`} onClick={() => setTipi((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={() => setTipi((arr) => [...arr, "Nuovo tipo"])} className="rounded-full gap-1.5 border-dashed mt-3" data-testid="add-tipo-button">
            <Plus className="h-4 w-4" /> Aggiungi tipo
          </Button>
        </Card>
      )}

      {!isRitr && (
        <Card className="p-6 animate-fade-up" data-testid="tipi-lavorazione-card">
          <h2 className="text-lg font-semibold mb-1">Tipi di lavorazione (Stato lavori Portfolio)</h2>
          <p className="text-xs text-slate-500 mb-4">Le voci rapide che puoi aggiungere alla lista "Stato lavori" di ogni società del Portfolio.</p>
          <div className="space-y-2">
            {lavTipi.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`lav-tipo-row-${i}`}>
                <Tag className="h-4 w-4 text-primary flex-shrink-0" />
                <Input data-testid={`lav-tipo-input-${i}`} value={t} onChange={(e) => setLavTipi((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))} className="flex-1" />
                <button data-testid={`lav-tipo-del-${i}`} onClick={() => setLavTipi((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={() => setLavTipi((arr) => [...arr, "Nuova lavorazione"])} className="rounded-full gap-1.5 border-dashed mt-3" data-testid="add-lav-tipo-button">
            <Plus className="h-4 w-4" /> Aggiungi lavorazione
          </Button>
        </Card>
      )}
    </div>
  );
}
